import { Injectable } from '@angular/core';
// NgRx
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
// RxJs
import { map, switchMap, tap } from 'rxjs/operators';

import { PlSessionService } from '@app/documentation/services';
// Actions
import * as actions from './actions';
// Services
import { PlClientDataService } from '../pl-client-data.service';
import { PlClientService } from '../pl-client.service';

@Injectable()
export class PlClientEffects {
  constructor(
    private actions$: Actions,
    private clientStore: PlClientService,
    private service: PlClientDataService,
    private sessionService: PlSessionService
  ) {}

  fetchEntities$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.PlFetchClientEntities),
        switchMap(({ clientUuid, entity_name, data }) => {
          this.clientStore.updateOneInCache({
            [entity_name]: [],
            uuid: clientUuid,
          });
          return this.service
            .getEntityService(entity_name, clientUuid)
            .get({
              params: {
                ...data,
              },
            })
            .pipe(
              tap((entities) =>
                this.clientStore.updateOneInCache({
                  [entity_name]: entities,
                  [`${entity_name}_loaded`]: true,
                  uuid: clientUuid,
                })
              )
            );
        })
      ),
    { dispatch: false }
  );

  fetchSessions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.PlFetchClientSessions),
        switchMap(({ clientUuid, start, end, iepId }) => {
          this.clientStore.updateOneInCache({
            sessions: [],
            uuid: clientUuid,
          });
          return this.sessionService
            .getClientSessions(clientUuid, { start, end, iepId })
            .pipe(
              tap((sessions) =>
                this.clientStore.updateOneInCache({
                  sessions,
                  sessions_loaded: true,
                  uuid: clientUuid,
                })
              )
            );
        })
      ),
    { dispatch: false }
  );

  addEntity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.PlAddClientEntity),
      switchMap(({ clientUuid, entity_name, data }) =>
        this.service
          .getEntityService(entity_name, clientUuid)
          .add(data)
          .pipe(
            map((res) =>
              actions.PlClientEntityAddedSuccessfully({
                clientUuid,
                entity_name,
                data: res,
              })
            )
          )
      )
    )
  );

  entityAdded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.PlClientEntityAddedSuccessfully),
        concatLatestFrom(({ clientUuid }) =>
          this.clientStore.selectEntityById(clientUuid)
        ),
        tap(([{ entity_name, data: newEntity }, client]) => {
          const existingEntities: any[] = (<any>client)[entity_name] || [];
          const parser = this.service.getEntityParser(entity_name);
          this.clientStore.updateOneInCache({
            ...client,
            [entity_name]: [parser(newEntity), ...existingEntities],
          });
        })
      ),
    { dispatch: false }
  );

  editEntity$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.PlEditClientEntity),
        concatLatestFrom(({ clientUuid }) =>
          this.clientStore.selectEntityById(clientUuid)
        ),
        switchMap(([{ clientUuid, entity_name, data }, client]) =>
          this.service
            .getEntityService<{ uuid: string }>(entity_name, clientUuid)
            .edit(data)
            .pipe(
              tap((editedEntity) => {
                const existingEntities: any[] = [
                  ...((<any>client)[entity_name] || []),
                ];
                const index = existingEntities.findIndex(
                  (c) => c.uuid === editedEntity.uuid
                );
                const parser = this.service.getEntityParser(entity_name);
                existingEntities[index] = parser(editedEntity);
                this.clientStore.updateOneInCache({
                  ...client,
                  [entity_name]: existingEntities,
                });
              })
            )
        )
      ),
    { dispatch: false }
  );

  deleteEntity$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.PlDeleteClientEntity),
        concatLatestFrom(({ clientUuid }) =>
          this.clientStore.selectEntityById(clientUuid)
        ),
        switchMap(([{ clientUuid, entity_name, data }, client]) =>
          this.service
            .getEntityService(entity_name, clientUuid)
            .delete(data)
            .pipe(
              tap(() => {
                const entities = [
                  ...(<any>client)[entity_name].filter(
                    (c: { uuid?: string }) => c.uuid !== data
                  ),
                ];
                this.clientStore.updateOneInCache({
                  ...client,
                  [entity_name]: entities,
                });
              })
            )
        )
      ),
    { dispatch: false }
  );

  deleteObjective$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.PlDeleteClientObjective),
        switchMap(({ clientUuid, objectiveUuid }) =>
          this.service
            .deleteObjective(clientUuid, objectiveUuid)
            .pipe(tap(() => this.clientStore.getGoals(clientUuid)))
        )
      ),
    { dispatch: false }
  );

  deleteSessionObjective$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.PlDeleteSessionObjective),
        concatLatestFrom(({ clientUuid }) =>
          this.clientStore.selectEntityById(clientUuid)
        ),
        switchMap(
          ([{ sessionObjectiveUuid, eventUuid, sessionUuid }, client]) =>
            this.sessionService
              .deleteSessionObjective(sessionUuid, sessionObjectiveUuid)
              .pipe(
                tap(() => {
                  let eventSession = client.sessions.find(
                    (e) =>
                      e.event_uuid === eventUuid &&
                      e.session_uuid === sessionUuid
                  );
                  eventSession = {
                    ...eventSession,
                    objectives: eventSession.objectives.filter(
                      (o) => o.uuid !== sessionObjectiveUuid
                    ),
                  };
                  this.clientStore.updateOneInCache({
                    ...client,
                    sessions: [
                      ...client.sessions.filter(
                        (e) =>
                          e.event_uuid !== eventUuid &&
                          e.session_uuid !== sessionUuid
                      ),
                      eventSession,
                    ],
                    goals_loaded: false,
                  });
                })
              )
        )
      ),
    { dispatch: false }
  );
}
